import React, { createContext, useContext, useEffect, useState } from 'react'
import { notificationManager, type RestaurantNotification } from '@/services/pushNotificationService'
import { useAuth } from './AuthContext'

interface NotificationContextType {
  isSupported: boolean
  isSubscribed: boolean
  permission: NotificationPermission
  initialize: () => Promise<boolean>
  sendNotification: (notification: RestaurantNotification) => Promise<void>
  scheduleNotification: (notification: RestaurantNotification, delay?: number) => Promise<void>
  toggleSubscription: () => Promise<boolean>
  sendTestNotification: () => Promise<void>
}

const NotificationContext = createContext<NotificationContextType | null>(null)

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [isSupported, setIsSupported] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [permission, setPermission] = useState<NotificationPermission>('default')

  useEffect(() => {
    // Check if notifications are supported
    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
    setIsSupported(supported)

    if (supported) {
      setPermission(Notification.permission)
      checkSubscriptionStatus()
    }
  }, [])

  useEffect(() => {
    // Initialize notifications for logged-in users
    if (user && isSupported) {
      initializeNotifications()
    }
  }, [user, isSupported])

  const checkSubscriptionStatus = async () => {
    try {
      const status = await notificationManager.getSubscriptionStatus()
      setIsSubscribed(status.subscribed)
    } catch (error) {
      console.error('Failed to check subscription status:', error)
    }
  }

  const initializeNotifications = async () => {
    try {
      const initialized = await notificationManager.initialize()
      if (initialized) {
        setIsSubscribed(true)
        setPermission('granted')
        
        // Set up subscription expiry notifications for restaurant owners
        if (user?.userType === 'restaurant_owner' && user.subscriptionEndDate) {
          setupSubscriptionNotifications()
        }
      }
    } catch (error) {
      console.error('Failed to initialize notifications:', error)
    }
  }

  const setupSubscriptionNotifications = () => {
    if (!user?.subscriptionEndDate) return

    const endDate = new Date(user.subscriptionEndDate)
    const now = new Date()
    const daysUntilExpiry = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    // Schedule notifications for 30, 7, and 1 day before expiry
    const notificationSchedule = [30, 7, 1]

    notificationSchedule.forEach(days => {
      if (daysUntilExpiry === days) {
        const notification: RestaurantNotification = {
          type: 'subscription_expiry',
          title: 'Subscription Expiring Soon',
          message: `Your subscription expires in ${days} day${days > 1 ? 's' : ''}. Contact admin to renew.`,
          urgency: days <= 3 ? 'high' : 'normal',
          data: { daysRemaining: days }
        }

        // Send immediately if we're at the threshold
        scheduleNotification(notification, 1000)
      }
    })
  }

  const initialize = async (): Promise<boolean> => {
    try {
      const result = await notificationManager.initialize()
      if (result) {
        setIsSubscribed(true)
        setPermission('granted')
      }
      return result
    } catch (error) {
      console.error('Failed to initialize notifications:', error)
      return false
    }
  }

  const sendNotification = async (notification: RestaurantNotification): Promise<void> => {
    try {
      await notificationManager.sendNotification(notification)
    } catch (error) {
      console.error('Failed to send notification:', error)
      throw error
    }
  }

  const scheduleNotification = async (notification: RestaurantNotification, delay: number = 0): Promise<void> => {
    try {
      await notificationManager.scheduleNotification(notification, delay)
    } catch (error) {
      console.error('Failed to schedule notification:', error)
      throw error
    }
  }

  const toggleSubscription = async (): Promise<boolean> => {
    try {
      const result = await notificationManager.toggleSubscription()
      setIsSubscribed(result)
      if (result) {
        setPermission('granted')
      }
      return result
    } catch (error) {
      console.error('Failed to toggle subscription:', error)
      return false
    }
  }

  const sendTestNotification = async (): Promise<void> => {
    const testNotification: RestaurantNotification = {
      type: 'system_alert',
      title: 'Test Notification',
      message: 'This is a test notification from Restaurant Analytics. Push notifications are working!',
      urgency: 'normal',
      data: { test: true }
    }

    await sendNotification(testNotification)
  }

  const value: NotificationContextType = {
    isSupported,
    isSubscribed,
    permission,
    initialize,
    sendNotification,
    scheduleNotification,
    toggleSubscription,
    sendTestNotification
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

export { NotificationContext }